/**
 * Applies DRM policies (context-menu block, copy/print keyboard-shortcut
 * block, drag block) to an epub.js spine-item iframe after it's been
 * rendered.
 *
 * epub.js exposes `rendition.hooks.content`, which fires with a `Contents`
 * object every time a spine item is loaded into an iframe. This module's
 * `applyIframeDrmPolicies` is a pure function designed to be registered once
 * on that hook.
 *
 * TEXT SELECTION IS INTENTIONALLY ALLOWED. Earlier revisions of this file
 * injected `user-select: none !important` + a `selectstart` event blocker,
 * which prevented the user from ever creating a highlight: Phase 4's
 * selection menu can't capture what the browser never lets select. The
 * top-level `useDrmProtection` hook documents this contract ("Allow
 * selection for highlights/notes"). DRM is still enforced via the
 * clipboard path — `copy`, `cut`, `dragstart`, and Ctrl+C/P/S/U / F12
 * keyboard shortcuts are blocked. A user can highlight a passage but
 * cannot copy its text off the page.
 *
 * Parity notes with `useDrmProtection.ts` (which handles the top-level window):
 *   - `contextmenu` blocked — same.
 *   - `keydown` with Ctrl+C / Ctrl+P / Ctrl+S / Ctrl+U / F12 blocked — same.
 *   - `copy`, `cut`, `dragstart` events preventDefault — same spirit.
 *   - `selectstart` NOT blocked; `user-select: none` NOT injected. Selection
 *     is the user-affordance for creating highlights; blocking it would
 *     break Phase 4 entirely.
 *   - DevTools size-check is NOT replicated inside the iframe — it wouldn't
 *     detect anything meaningful because the iframe doesn't have its own
 *     DevTools attach surface; the top-level hook handles it for the whole
 *     tab.
 */

interface EpubJsContents {
  document: Document;
  window: Window;
}

const BLOCKED_KEYS = new Set(['c', 'p', 's', 'u']);

/**
 * Registers the listeners on a single spine-item iframe document.
 * Idempotent per call — re-registering on the same document would duplicate
 * listeners, so epub.js only invokes the hook once per iframe load which is
 * the intended flow.
 */
export function applyIframeDrmPolicies(contents: EpubJsContents): void {
  const { document: iframeDoc } = contents;

  // Block right-click context menu.
  iframeDoc.addEventListener('contextmenu', preventDefault, { capture: true });

  // Clipboard escape routes — block explicitly so selection stays, but the
  // user can't exfiltrate text through standard copy/cut or drag-image.
  iframeDoc.addEventListener('copy', preventDefault, { capture: true });
  iframeDoc.addEventListener('cut', preventDefault, { capture: true });
  iframeDoc.addEventListener('dragstart', preventDefault, { capture: true });

  // Block keyboard shortcuts that trigger print / view-source / save /
  // devtools.
  iframeDoc.addEventListener('keydown', handleKeydown, { capture: true });
}

function preventDefault(e: Event): void {
  e.preventDefault();
  e.stopPropagation();
}

function handleKeydown(e: KeyboardEvent): void {
  // F12 → DevTools
  if (e.key === 'F12') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  // Ctrl/Cmd combos: C (copy), P (print), S (save), U (view-source)
  const ctrlish = e.ctrlKey || e.metaKey;
  if (ctrlish && BLOCKED_KEYS.has(e.key.toLowerCase())) {
    e.preventDefault();
    e.stopPropagation();
  }
}
